/* eslint-disable @typescript-eslint/no-use-before-define */
import { useRef } from 'react';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { rgba } from 'polished';
import styled, { css, useTheme } from 'styled-components';

import { rotate } from '../../../utils/animations';

import { MAGIC_ELEMENT_SIZES, type MagicElementState } from './sharedStyles';

const StillThinkingAnimationContainer = styled.div`
  padding-right: 5px;
  overflow: hidden;

  white-space: nowrap;

  opacity: 0;

  transition: opacity
    ${({ theme }) =>
      theme.animation.durationMedium + ' ' + theme.animation.EASING};

  &.still-thinking-enter {
    opacity: 0;
  }
  &.still-thinking-enter-active,
  &.still-thinking-enter-done,
  &.still-thinking-exit {
    opacity: 1;
  }
  &.still-thinking-exit-active,
  &.still-thinking-exit-done {
    opacity: 0;
  }
`;

const Wrapper = styled.div<{
  $hasHover: boolean;
  $state: MagicElementState;
  $stillThinking: boolean;
}>`
  overflow: hidden;
  display: flex;
  flex: none;
  align-items: center;
  width: ${({ $stillThinking }) =>
    $stillThinking
      ? `${MAGIC_ELEMENT_SIZES.StillThinking}px`
      : `${MAGIC_ELEMENT_SIZES.Default}px`};
  height: ${MAGIC_ELEMENT_SIZES.Default}px;
  gap: 3px;

  color: ${({ theme }) => theme.appUI.magic.primaryColor};
  font-size: ${({ theme }) => theme.fontSize.SMALL};

  border-radius: ${({ theme }) => theme.borderRadius};

  transition:
    background-color
      ${({ theme }) => theme.animation.DURATION + ' ' + theme.animation.EASING},
    width
      ${({ theme }) =>
        theme.animation.durationMedium + ' ' + theme.animation.EASING};

  ${({ $hasHover }) =>
    $hasHover &&
    css`
      cursor: pointer;

      @media (hover: hover) {
        &:hover {
          background: ${({ theme }) =>
            rgba(theme.appUI.magic.primaryColor, 0.1)};
        }
      }
    `}
`;

const VerticalCircles = styled.div`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 50%;

  display: flex;
  flex-direction: column;
  align-items: center;

  width: 18px;
  margin-left: -9px;

  transition:
    top
      ${({ theme }) => theme.animation.DURATION + ' ' + theme.animation.EASING},
    bottom
      ${({ theme }) => theme.animation.DURATION + ' ' + theme.animation.EASING};
`;

const HorizontalCircles = styled.div`
  position: absolute;
  top: 50%;

  right: 3px;
  left: 3px;

  display: flex;
  align-items: center;

  height: 18px;
  margin-top: -9px;

  transition:
    left
      ${({ theme }) => theme.animation.DURATION + ' ' + theme.animation.EASING},
    right
      ${({ theme }) => theme.animation.DURATION + ' ' + theme.animation.EASING},
    opacity
      ${({ theme }) => theme.animation.DURATION + ' ' + theme.animation.EASING};
`;

const Circle = styled.div`
  position: absolute;

  flex: none;

  width: 18px;
  height: 18px;

  border-radius: 50%;

  transition:
    box-shadow
      ${({ theme }) => theme.animation.DURATION + ' ' + theme.animation.EASING},
    width
      ${({ theme }) => theme.animation.DURATION + ' ' + theme.animation.EASING},
    height
      ${({ theme }) => theme.animation.DURATION + ' ' + theme.animation.EASING};
`;

const TopCircle = styled(Circle)`
  top: 0;
`;

const BottomCircle = styled(Circle)`
  bottom: 0;
`;

const LeftCircle = styled(Circle)`
  left: 0;
`;

const RightCircle = styled(Circle)`
  right: 0;
`;

const Icon = styled.div<{
  $color: 'light' | 'dark';
  $state: MagicElementState;
  $stillThinking: boolean;
}>`
  display: flex;
  flex: none;
  width: ${MAGIC_ELEMENT_SIZES.Default}px;
  height: ${MAGIC_ELEMENT_SIZES.Default}px;

  position: relative;

  ${Circle} {
    box-shadow: inset 0 0 0 1px
      ${({ theme, $color }) =>
        $color === 'light'
          ? theme.appUI.magic.primaryColor
          : theme.highlightColor};
  }

  ${({ $state }) =>
    $state === 'default' &&
    css`
      ${VerticalCircles} {
        top: 6px;
        bottom: 6px;
      }

      ${HorizontalCircles} {
        opacity: 0;
      }

      ${Circle} {
        width: 14px;
        height: 14px;
      }
    `}

  ${({ $state, theme, $color }) =>
    $state === 'active' &&
    css`
      /* stylelint-disable-next-line no-duplicate-selectors */
      ${HorizontalCircles} {
        opacity: 0.75;
      }

      ${LeftCircle},
      ${RightCircle} {
        box-shadow: inset 0 0 0 1px
          ${$color === 'light'
            ? theme.appUI.magic.primaryColor
            : theme.highlightColor};
      }
    `}

  ${({ $state, $color, theme }) =>
    $state === 'loading' &&
    css`
      /* stylelint-disable-next-line no-duplicate-selectors */
      ${VerticalCircles} {
        animation: ${rotate} 3s linear infinite;
      }

      /* stylelint-disable-next-line no-duplicate-selectors */
      ${HorizontalCircles} {
        opacity: 0.6;

        animation: ${rotate} 2s linear infinite;
      }

      /* stylelint-disable-next-line no-duplicate-selectors */
      ${LeftCircle},
      ${RightCircle} {
        ${RightCircle} {
        box-shadow: inset 0 0 0 1px
          ${
            $color === 'light'
              ? theme.appUI.magic.secondaryColor
              : theme.highlightColor
          };
      }
    `}
`;
interface MagicButtonProps {
  color?: 'light' | 'dark';
  onClick?: () => void;
  state?: MagicElementState;
  stillThinking?: boolean;
}

export const MagicButton: React.ComponentType<MagicButtonProps> = React.memo(
  function MagicButton({
    state = 'default',
    stillThinking = false,
    onClick,
    color = 'light'
  }) {
    const theme = useTheme();
    const stillThinkingRef = useRef<HTMLDivElement>(null);

    return (
      <Wrapper
        $hasHover={Boolean(onClick)}
        $state={state}
        $stillThinking={stillThinking}
        onClick={onClick}>
        <Icon $color={color} $state={state} $stillThinking={stillThinking}>
          <VerticalCircles>
            <TopCircle />
            <BottomCircle />
          </VerticalCircles>
          <HorizontalCircles>
            <LeftCircle />
            <RightCircle />
          </HorizontalCircles>
        </Icon>
        <CSSTransition
          appear={true}
          classNames="still-thinking"
          in={stillThinking}
          mountOnEnter={true}
          nodeRef={stillThinkingRef}
          timeout={theme.animation.durationMediumMs}
          unmountOnExit={true}>
          <StillThinkingAnimationContainer ref={stillThinkingRef}>
            Thinking...
          </StillThinkingAnimationContainer>
        </CSSTransition>
      </Wrapper>
    );
  }
);
